import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { carriersApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface FormState { id?: string; code: string; name: string; phone: string; isActive: boolean }
const blank = (): FormState => ({ code: "", name: "", phone: "", isActive: true });

export function CarriersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank());
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["carriers"],
    queryFn: () => carriersApi.list({ pageSize: 200 }),
  });

  const startCreate = () => { setForm(blank()); setError(null); setOpen(true); };
  const startEdit = (id: string) => {
    const c = query.data?.items.find(x => x.id === id);
    if (!c) return;
    setForm({ id: c.id, code: c.code, name: c.name, phone: c.phone ?? "", isActive: c.isActive });
    setError(null); setOpen(true);
  };
  const save = useMutation({
    mutationFn: async () => {
      if (form.id) {
        await carriersApi.update(form.id, {
          id: form.id, code: form.code, name: form.name,
          phone: form.phone || undefined, isActive: form.isActive,
        });
      } else {
        await carriersApi.create({
          code: form.code, name: form.name, phone: form.phone || undefined,
        });
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["carriers"] }); setOpen(false); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => carriersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["carriers"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Carriers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>New carrier</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Code</TableCell><TableCell>Name</TableCell>
            <TableCell>Phone</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {query.data?.items.map(c => (
              <TableRow key={c.id} hover>
                <TableCell><b>{c.code}</b></TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.phone ?? "—"}</TableCell>
                <TableCell>
                  <Chip size="small" color={c.isActive ? "success" : "default"}
                    label={c.isActive ? "Active" : "Inactive"} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => startEdit(c.id)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error"
                    onClick={() => { if (confirm(`Delete carrier ${c.code}?`)) del.mutate(c.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? `Edit carrier ${form.code}` : "New carrier"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Code *" value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} />
            <TextField label="Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField label="Phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
            {form.id && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <Typography>{form.isActive ? "Active" : "Inactive"}</Typography>
              </Stack>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.code || !form.name || save.isPending}
            onClick={() => save.mutate()}>{form.id ? "Save" : "Create"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
